import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

/**
 * Represents the name of an entity collection.
 * Either a {@link Name.Predefined predefined} or a
 * {@link Name.Dynamic dynamic} collection name.
 */
export type Name = Name.Predefined | Name.Dynamic
export namespace Name {
  export const isName: TypeChecker<Name> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.union(Name.Predefined.isPredefined, Dynamic.isDynamic),
  )
  export const assertIsCollectionName = TypeAsserter.Factory.from(isName)
  /**
   * Lists all predefined entity collection names in the platform.
   *
   * Benefits:
   * 1. Enforces stricter typing when retrieving an entity collection by name.
   * 2. Helps avoid name collisions.
   * 3. Promotes consistent naming conventions.
   */
  export enum Predefined {
    AgentKnowledgeBaseFile = 'AgentKnowledgeBaseFile',
    AdminApp = 'AdminApp',
    AdminAppPage = 'AdminAppPage',
    AdminPage = 'AdminPage',
    AdminScenario = 'AdminScenario',
    AdminTenant = 'AdminTenant',
    AdminUser = 'AdminUser',
    AdminZone = 'AdminZone',
    IntegrationConnector = 'IntegrationConnector',
    IntegrationConnectorDefinition = 'IntegrationConnectorDefinition',
    Clipboard = 'Clipboard',
    DmsAttribute = 'DmsAttribute',
    DmsDimension = 'DmsDimension',
    DmsEnum = 'DmsEnum',
    DmsEnumEntry = 'DmsEnumEntry',
    DmsEntity = 'DmsEntity',
    DmsModel = 'DmsModel',
    DmsRecord = 'DmsRecord',
    DmsSnapshot = 'DmsSnapshot',
    IdcFunctionCategory = 'IdcFunctionCategory',
    IdcFunctionSubcategory = 'IdcFunctionSubcategory',
    MosaicAction = 'MosaicAction',
    MosaicAppPageRenderingCollection = 'MosaicAppPageRenderingCollection',
    MosaicPage = 'MosaicPage',
    MosaicWidget = 'MosaicWidget',
    QuiverPackage = 'QuiverPackage',
    QuiverResource = 'QuiverResource',
    RateAdjustmentLabel = 'RateAdjustmentLabel',
    RateAllocationDistribution = 'RateAllocationDistribution',
    RateExclusionLabel = 'RateExclusionLabel',
    RateGlMappingEntry = 'RateGlMappingEntry',
    RateGlRecord = 'RateGlRecord',
    RateReport = 'RateReport',
    RateRule = 'RateRule',
    RelayProcess = 'RelayProcess',
    SpaceCluster = 'SpaceCluster',
    SpaceClusterAssignableRecord = 'SpaceClusterAssignableRecord',
    SpaceClusterAssignedRecord = 'SpaceClusterAssignedRecord',
    SpaceClusterOccupant = 'SpaceClusterOccupant',
    SpaceClusterResponsiblePerson = 'SpaceClusterResponsiblePerson',
    SpaceEmployee = 'SpaceEmployee',
    SpaceFunction = 'SpaceFunction',
    SpaceOccupant = 'SpaceOccupant',
    SpaceOccupantPayrollEntries = 'SpaceOccupantPayrollEntries',
    SpaceOrgAssignment = 'SpaceOrgAssignment',
    SpaceOrgAssignmentLite = 'SpaceOrgAssignmentLite',
    SpaceOrganization = 'SpaceOrganization',
    SpacePeople = 'SpacePeople',
    SpacePeopleLite = 'SpacePeopleLite',
    SpaceRecord = 'SpaceRecord',
    SpaceRecordLite = 'SpaceRecordLite',
    SpaceReport = 'SpaceReport',
    SpaceResponsiblePerson = 'SpaceResponsiblePerson',
    SpaceLabAssignment = 'SpaceLabAssignment',
    SpaceLabAssignableRecord = 'SpaceLabAssignableRecord',
    SpaceRoomType = 'SpaceRoomType',
    SpaceAssignableUser = 'SpaceAssignableUser',
    SpaceServiceCenter = 'SpaceServiceCenter',
    SpaceStandardUser = 'SpaceStandardUser',
    SpaceSurveyAdmin = 'SpaceSurveyAdmin',
    SpaceUnassignedSponsoredAccount = 'SpaceUnassignedSponsoredAccount',
    SpaceUnassignedSponsoredPerson = 'SpaceUnassignedSponsoredPerson',
    SpaceJobTitle = 'SpaceJobTitle',
  }
  // Needs to be before namespace is defined
  const PREDEFINED: ReadonlySet<Predefined> = new Set(
    Object.values(Predefined) as Predefined[],
  )
  export namespace Predefined {
    export const ALL: readonly Predefined[] = Array.from(PREDEFINED)
    export const isPredefined = TypeChecker.Factory.oneOf(PREDEFINED)
    export const assertIsPredefined: TypeAsserter<Predefined> =
      TypeAsserter.Factory.from(isPredefined)
  }

  /**
   * Represents a dynamic entity collection name, typically used when an entity
   * collection needs to be created dynamically.
   */
  export type Dynamic<TName extends string = string> =
    `${typeof Dynamic.PREFIX}${TName}`
  export namespace Dynamic {
    /**
     * The prefix used to identify dynamic entity collection names.
     */
    export const PREFIX = '$'

    export const isDynamic: TypeChecker<Dynamic> = TypeChecker.Factory.custom(
      (name: string): name is Dynamic => name.startsWith(PREFIX),
      { typeDescription: 'CollectionName.Dynamic' },
    )
    /**
     * Creates a dynamic entity collection name.
     *
     * @example
     * const name = CollectionName.Dynamic.from('MyDynamicCollection')
     * console.log(name)  // '$MyDynamicCollection'
     */
    export function from<TName extends string>(name: TName): Dynamic<TName> {
      return `${PREFIX}${name}`
    }
  }
}
